import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "gs_wishlist";

function readIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
  // Notify same-tab listeners
  window.dispatchEvent(new Event("gs_wishlist_change"));
}

/**
 * Persistent product wishlist keyed by product id.
 * Returns { ids, has, toggle, remove, clear } — ordered most-recent first.
 */
export default function useWishlist() {
  const [ids, setIds] = useState(readIds);

  useEffect(() => {
    const onChange = () => setIds(readIds());
    // Cross-tab
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) onChange();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("gs_wishlist_change", onChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("gs_wishlist_change", onChange);
    };
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    if (!id) return false;
    const current = readIds();
    const already = current.includes(id);
    const next = already ? current.filter((x) => x !== id) : [id, ...current];
    writeIds(next);
    setIds(next);
    return !already; // true when newly added
  }, []);

  const remove = useCallback((id) => {
    const next = readIds().filter((x) => x !== id);
    writeIds(next);
    setIds(next);
  }, []);

  const clear = useCallback(() => {
    writeIds([]);
    setIds([]);
  }, []);

  return { ids, has, toggle, remove, clear };
}
